import dayjs, { Dayjs } from 'dayjs';
import { DateType, OverviewDateType } from './const';

/**
 * 获取默认时间
 */
export const getDefaultTime = (mode: DateType | OverviewDateType, date?: Dayjs | null) => {
  switch (mode) {
    case DateType.DAY:
      return {
        startTime: dayjs(date).startOf('day').valueOf(),
        endTime: dayjs(date).endOf('day').valueOf(),
      };
    case DateType.MONTH:
      return {
        startTime: dayjs(date).startOf('month').valueOf(),
        endTime: dayjs(date).endOf('month').valueOf(),
      };

    default:
      return {
        startTime: dayjs(date).startOf('year').valueOf(),
        endTime: dayjs(date).endOf('year').valueOf(),
      };
  }
};

/**
 * 根据月周期设置获取时间范围
 * @param mode 时间颗粒度
 * @param startDate 开始时间
 * @param endDate 结束时间
 * @param monthPeriodType 月周期类型
 * @param monthPeriodStart 月周期起始日
 * @param monthPeriodEnd 月周期结束日
 */
export const getTimeRangeByMonthPeriod = (
  mode: DateType,
  startDate: Dayjs | null | undefined,
  endDate: Dayjs | null | undefined,
  monthPeriodType: string,
  monthPeriodStart: number,
  monthPeriodEnd: number
): { startTime: number; endTime: number } => {
  if (!startDate || !endDate) {
    return { startTime: 0, endTime: 0 };
  }

  const isNatural = monthPeriodType === 'natural';

  if (mode === DateType.MONTH) {
    if (isNatural) {
      return {
        startTime: startDate.startOf('month').valueOf(),
        endTime: endDate.endOf('month').valueOf(),
      };
    }
    // 自定义周期：
    // startTime = 开始日期的上月startDay日
    // endTime = 结束日期的本月endDay日
    const periodStart = startDate.clone().subtract(1, 'month').date(monthPeriodStart).startOf('day');
    const periodEnd = endDate.clone().date(monthPeriodEnd).endOf('day');
    return { startTime: periodStart.valueOf(), endTime: periodEnd.valueOf() };
  }

  if (mode === DateType.YEAR) {
    if (isNatural) {
      return {
        startTime: startDate.startOf('year').valueOf(),
        endTime: endDate.endOf('year').valueOf(),
      };
    }
    // 自定义周期：
    // startTime = 开始日期的上年12月startDay日
    // endTime = 结束日期的本年12月endDay日
    const periodStart = startDate.clone().subtract(1, 'year').month(11).date(monthPeriodStart).startOf('day');
    const periodEnd = endDate.clone().month(11).date(monthPeriodEnd).endOf('day');
    return { startTime: periodStart.valueOf(), endTime: periodEnd.valueOf() };
  }

  // 按日不受月周期影响
  return {
    startTime: startDate.startOf('day').valueOf(),
    endTime: endDate.endOf('day').valueOf(),
  };
};

/**
 * 构建 API 请求参数
 * - 按日查询：只传递部门/区域参数，不传递月周期参数
 * - 按月/按年查询：传递完整参数（部门/区域 + 月周期）
 * - 即使 deptAreaId 为空，只要 reportScope 是部门或区域，就传递 deptOrArea
 */
export const buildMonthCycleParams = (
  timeResolution: DateType,
  monthCycleApiParams?: {
    deptAreaId?: number;
    monthCycleType: number;
    monthCycleFrom: number;
    monthCycleTo: number;
  },
  reportScope?: 'factory' | 'dept' | 'area'
) => {
  // 全厂或不传 reportScope 时，不传递参数
  if (!reportScope || reportScope === 'factory') {
    return {};
  }

  // 基础参数：部门/区域
  const baseParams = {
    deptOrArea: reportScope === 'dept' ? 1 : reportScope === 'area' ? 2 : undefined,
    deptAreaId: monthCycleApiParams?.deptAreaId, // 可能为 undefined
  };

  // 按日查询：只传递部门/区域参数，不传递月周期参数
  if (timeResolution === DateType.DAY) {
    return baseParams;
  }

  // 按月/按年查询：传递完整参数（部门/区域 + 月周期）
  const monthCycleParams: any = {
    monthCycleType: monthCycleApiParams?.monthCycleType,
  };

  // 仅当月周期类型为自定义（2）时，才传递 monthCycleFrom 和 monthCycleTo
  if (monthCycleApiParams?.monthCycleType === 2) {
    monthCycleParams.monthCycleFrom = monthCycleApiParams?.monthCycleFrom;
    monthCycleParams.monthCycleTo = monthCycleApiParams?.monthCycleTo;
  }

  return {
    ...baseParams,
    ...monthCycleParams,
  };
};
